import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Salary from "./salaries/salaries";
import Paycheck from "./paycheck/paycheck";
import useTour from "../../components/tour/useTour";
import GuidedTour from "../../components/tour/GuidedTour";
import getSalariesTourSteps from "./salaries/salariesTourSteps";
import getPaycheckTourSteps from "./paycheck/paycheckTourSteps";
import "./SalariesTaxWrapper.scss";

const TABS = [
  {
    key: "salary",
    label: "Hospital",
  },
  {
    key: "paycheck",
    label: "Paycheck",
    subtext: "Calculate take-home from any salary",
  },
];

// Accept ?tab=paycheck as a friendlier alias for the internal ?tab=tax key.
// Both forms exist in the codebase: the saved-paychecks list and
// paycheck.js's post-load cleanup both use ?tab=paycheck.
const isPaycheckTab = (t) => t === "tax" || t === "paycheck";

const SalariesTaxWrapper = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  // const [activeTab, setActiveTab] = useState(
  //   isPaycheckTab(tabFromUrl) ? "tax" : "salary",
  // );
  const [activeTab, setActiveTab] = useState(
    isPaycheckTab(tabFromUrl) === "paycheck" ? "paycheck" : "salary",
  );

  const stateName = useSelector((s) => s.salaries?.salariesInputData?.stateName || "All States");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    setActiveTab(isPaycheckTab(searchParams.get("tab")) ? "paycheck" : "salary");
  }, [searchParams]);

  // Guided tours — one per tab. Each auto-starts the first time its own tab
  // is active (and not yet completed), and can be replayed from the header
  // button (which starts the tour matching the current tab). Gating
  // autoStart to the active tab prevents both tours opening at once.
  const salariesTourSteps = useMemo(() => getSalariesTourSteps(), []);
  const paycheckTourSteps = useMemo(() => getPaycheckTourSteps(), []);
  const { isOpen: isSalaryTourOpen, finish: finishSalaryTour } = useTour(
    "salaries",
    { autoStart: activeTab === "salary" },
  );
  const { isOpen: isPaycheckTourOpen, finish: finishPaycheckTour } = useTour(
    "paycheck",
    { autoStart: activeTab === "paycheck" },
  );

  const activeSubtext = activeTab === "salary"
    ? `${stateName} hospital union rates · RN positions`
    : TABS.find((t) => t.key === activeTab)?.subtext;

  return (
    <div className="salaries-tax-wrapper">
      {/* SEGMENTED TAB CONTROL */}
      <div className="segmented-tabs-wrap">
        <div
          className="segmented-tabs"
          role="tablist"
          data-active={activeTab}
          data-tour="salary-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={`segmented-tab ${activeTab === t.key ? "is-active" : ""
                }`}
              onClick={() => handleTabChange(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {activeSubtext && (
          <div className="segmented-subtext">{activeSubtext}</div>
        )}
      </div>

      {/* TAB CONTENT */}
      <div className="tab-content" role="tabpanel">
        {activeTab === "salary" && <Salary />}
        {activeTab === "paycheck" && <Paycheck />}
      </div>

      <GuidedTour
        isOpen={isSalaryTourOpen && activeTab === "salary"}
        steps={salariesTourSteps}
        onDone={finishSalaryTour}
      />
      <GuidedTour
        isOpen={isPaycheckTourOpen && activeTab === "paycheck"}
        steps={paycheckTourSteps}
        onDone={finishPaycheckTour}
      />
    </div>
  );
};

export default SalariesTaxWrapper;
